import React from 'react';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { SearchPicker } from './SearchPicker';
import { LayoutGrid, X, BarChart3, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith('/') ? `${API_URL}${u}` : u);
const minPrice = (p) => {
  const ps = (p.variants || []).map((v) => Number(v.price) || 0).filter(Boolean);
  return ps.length ? Math.min(...ps) : Number(p.price) || 0;
};

export const BroadcastFeaturedProducts = ({ adminFetch, ids, setIds }) => {
  const [products, setProducts] = React.useState([]);
  React.useEffect(() => {
    adminFetch(`${API_URL}/api/admin/products`).then((r) => r.json()).then((d) => setProducts(Array.isArray(d) ? d : (d.products || []))).catch(() => {});
  }, [adminFetch]);

  const options = React.useMemo(() => products.filter((p) => !ids.includes(p.product_id)).map((p) => ({
    key: p.product_id, label: p.name, sublabel: `From $${minPrice(p).toFixed(2)}`, search: `${p.name} ${p.category || ''}`,
  })), [products, ids]);
  const picked = ids.map((id) => products.find((p) => p.product_id === id)).filter(Boolean);

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="broadcast-featured-section">
      <Label className="text-sm inline-flex items-center gap-1"><LayoutGrid className="h-4 w-4 text-teal-600" /> Featured products (optional, up to 3)</Label>
      {picked.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {picked.map((p) => (
            <div key={p.product_id} className="relative rounded-lg border bg-slate-50 p-2 text-center" data-testid={`broadcast-featured-item-${p.product_id}`}>
              <button type="button" onClick={() => setIds(ids.filter((i) => i !== p.product_id))} className="absolute top-1 right-1 rounded-full bg-white border p-0.5 text-slate-500 hover:text-red-600" data-testid={`broadcast-featured-remove-${p.product_id}`}><X className="h-3 w-3" /></button>
              {p.image_url ? <img src={imgSrc(p.image_url)} alt={p.name} className="h-16 w-full object-contain mb-1" /> : <div className="h-16 mb-1 rounded bg-slate-200" />}
              <p className="text-xs font-semibold text-slate-800 truncate">{p.name}</p>
              <p className="text-[11px] text-teal-700">From ${minPrice(p).toFixed(2)}</p>
            </div>
          ))}
        </div>
      )}
      {ids.length < 3 && (
        <SearchPicker options={options} value="" onSelect={(k) => k && setIds([...ids, k].slice(0, 3))} placeholder="+ Add a product" searchPlaceholder="Search products…" emptyText="No product found" testId="broadcast-featured-picker" />
      )}
      <p className="text-xs text-slate-400">Shows a row with photo, price and a Shop now button, above the discount code.</p>
    </div>
  );
};

const fmt = (s) => { try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return '—'; } };

export const BroadcastReportDialog = ({ adminFetch, broadcastId, onClose }) => {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!broadcastId) return;
    setData(null);
    adminFetch(`${API_URL}/api/admin/broadcast/${broadcastId}/report`).then((r) => r.json()).then(setData).catch(() => setData({ orders: [], totals: {} }));
  }, [broadcastId, adminFetch]);
  const t = data?.totals || {};
  const stats = [
    ['Revenue', `$${Number(t.revenue || 0).toFixed(2)}`], ['Orders', t.orders || 0],
    ['Customers', t.customers || 0], ['Redemption', `${t.redemption_rate || 0}%`],
  ];

  return (
    <Dialog open={!!broadcastId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="broadcast-report-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-teal-600" /> Code usage report</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-8 text-teal-600" /> : (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">{data.broadcast?.subject} · {data.broadcast?.unique_codes ? `${t.codes_issued || 0} personal codes issued · ${t.reminders_sent || 0} reminders sent` : (data.broadcast?.discount_code ? `Code ${data.broadcast.discount_code}` : 'No discount code')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {stats.map(([k, v]) => (
                <div key={k} className="rounded-lg bg-teal-50 border border-teal-100 p-3" data-testid={`broadcast-report-${k.toLowerCase()}`}>
                  <p className="text-[11px] uppercase tracking-wide text-teal-700">{k}</p>
                  <p className="text-lg font-bold text-slate-900">{v}</p>
                </div>
              ))}
            </div>
            {data.orders?.length ? (
              <div className="divide-y border rounded-lg" data-testid="broadcast-report-orders-list">
                {data.orders.map((o) => (
                  <div key={o.order_id} className="flex items-center justify-between gap-3 p-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{o.customer_name || o.customer_email}</p>
                      <p className="text-xs text-slate-500 truncate">{o.customer_email} · {o.code} · {fmt(o.created_at)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-slate-900">${o.total.toFixed(2)}</p>
                      {o.discount_amount > 0 && <p className="text-xs text-teal-700">-${o.discount_amount.toFixed(2)}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 py-4 text-center" data-testid="broadcast-report-empty">No one has used this broadcast's code yet.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
